import type { AnyID } from '@types'
import { isRelaxIntegratedUnitByDefaults } from '@utils/atemporal/relaxArmVariants'
import { isPouff, isRinconeroCatalog } from '@utils/sceneObjects/items/rinconeroUtils'

/** Catálogo de brazo BR* (incl. variantes de profundidad BR3-100 / BR3-105). */
export const ARM_CATALOG_RE = /^BR\d+(-\d+)?$/i

export const isArmCatalogId = (catalogID: unknown): boolean =>
  ARM_CATALOG_RE.test(String(catalogID || ''))

export const isArmSceneItem = (item: { catalogID?: unknown; codigo?: unknown } | null | undefined) =>
  Boolean(item && isArmCatalogId(item.catalogID ?? item.codigo))

/**
 * Unidades que no hospedan un BR* independiente (RELAX por GLB, rinconero, pouff).
 * Al dibujarlas junto a un brazo hay que colocarlas en el exterior del brazo
 * (o eliminar el brazo), nunca migrar el brazo al otro extremo.
 */
export const unitCanHostIndependentArm = (catalogID: unknown): boolean => {
  if (isArmCatalogId(catalogID)) return false
  if (isPouff(catalogID)) return false
  if (isRinconeroCatalog(catalogID)) return false
  if (isRelaxIntegratedUnitByDefaults(catalogID)) return false
  return true
}

type SnappedSides = Partial<Record<'left' | 'right', string>>

/**
 * Los brazos no ocupan labios de fila entre módulos.
 * `snappedSides` de una unidad base solo debe referenciar otras unidades (o nada).
 */
export function isArmOccupantId(
  occupantId: string | undefined | null,
  resolveItem: (id: string) => { catalogID?: unknown; codigo?: unknown } | null | undefined,
): boolean {
  if (!occupantId) return false
  const item = resolveItem(String(occupantId))
  return isArmSceneItem(item)
}

/** Devuelve el ocupante solo si es un módulo (nunca un brazo). */
export function resolveModuleSideOccupantId(
  occupantId: string | undefined | null,
  resolveItem: (id: string) => { catalogID?: unknown; codigo?: unknown } | null | undefined,
  excludeItemId?: string,
): string | undefined {
  if (!occupantId) return undefined
  const id = String(occupantId)
  if (excludeItemId && id === String(excludeItemId)) return undefined
  if (isArmOccupantId(id, resolveItem)) return undefined
  return id
}

/** Elimina IDs de brazo de `snappedSides` (limpieza de contaminación legacy). */
export function stripArmIdsFromSnappedSides(
  snappedSides: SnappedSides | null | undefined,
  resolveItem: (id: string) => { catalogID?: unknown; codigo?: unknown } | null | undefined,
): SnappedSides {
  const next: SnappedSides = { ...(snappedSides || {}) }
  for (const side of ['left', 'right'] as const) {
    const id = next[side]
    if (id && isArmOccupantId(id, resolveItem)) {
      delete next[side]
    }
  }
  return next
}

/**
 * Quita un brazo concreto del registro lateral de su antiguo padre
 * (p. ej. al migrarlo al extremo de la unidad recién dibujada).
 */
export function clearArmFromUnitSnappedSides(
  unit: { snappedSides?: SnappedSides } | null | undefined,
  armId: AnyID | string,
): void {
  if (!unit?.snappedSides) return
  const armKey = String(armId)
  const next: SnappedSides = { ...unit.snappedSides }
  let dirty = false
  for (const side of ['left', 'right'] as const) {
    if (next[side] && String(next[side]) === armKey) {
      delete next[side]
      dirty = true
    }
  }
  if (dirty) unit.snappedSides = next
}
